import { Pipe, PipeTransform } from '@angular/core';
import DOMPurify, { Config } from 'dompurify';

/**
 * Renders backend-provided HTML (e.g. journal details/values) while stripping
 * anything that can load external resources or execute. This blocks the
 * HTML-injection / external-image issue raised in the security review while
 * preserving legitimate formatting (links, bold, line breaks) that the backend
 * intentionally includes.
 *
 * DOMPurify already removes <script>, event handler attributes and unsafe URI
 * schemes (e.g. javascript:) by default. We additionally forbid tags that fetch
 * external content so injected <img>/<iframe>/etc. cannot trigger third-party
 * requests.
 *
 * The pipe returns the sanitized HTML as a plain string rather than a trusted
 * SafeHtml value. This intentionally lets Angular's built-in [innerHTML]
 * sanitizer run as a second layer of defence. Angular preserves anchor tags
 * (href/target/rel) and basic formatting, so legitimate links/bold/line breaks
 * still render while a DOMPurify misconfiguration or bypass remains contained.
 */
const SANITIZE_CONFIG: Config = {
  FORBID_TAGS: [
    'img',
    'picture',
    'source',
    'image',
    'svg',
    'video',
    'audio',
    'track',
    'iframe',
    'frame',
    'object',
    'embed',
    'link',
    'style',
    'base',
    'form',
    'input',
    'meta'
  ],
  FORBID_ATTR: ['style', 'src', 'srcset', 'poster', 'background', 'formaction']
};

@Pipe({ name: 'sanitizeHtml' })
export class SanitizeHtmlPipe implements PipeTransform {
  public transform(value: string | null | undefined): string {
    return value ? DOMPurify.sanitize(value, SANITIZE_CONFIG) : '';
  }
}
